import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import {
  AudioPlayerInput,
  Button,
  InputLabel,
} from '@wajeez-web/components/ui';
import { UploadFileFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useEffectCancelled } from '@wajeez-web/components/helpers';
import { AiOutlineLink } from 'react-icons/ai';
import { MediaHelpers } from '@wajeez-web/core/api';
import { GoCheck } from 'react-icons/go';
export interface FormUploadInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: UploadFileFormType;
  disabled?: boolean;
}

const FormUploadInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  ...props
}: FormUploadInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,

      ...props.rules,
    },
  });
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  const InputRef = React.createRef<HTMLInputElement>();
  const [src, setSrc] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const getFileView = () => {
    if (fieldType.fieldOptions.types?.find((item) => item.includes('audio'))) {
      return <AudioPlayerInput value={src} />;
    } else {
      return (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className={'flex space-s-2'}
        >
          <AiOutlineLink className="h-6 w-6" />
          <p>URL</p>
        </a>
      );
    }
  };
  const onChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    e.preventDefault();
    const file = e.target.files?.[0];

    if (file) {
      setIsLoading(true);
      try {
        const uploadMethod =
          fieldType.fieldOptions.uploadEndPoint === 'Public'
            ? MediaHelpers.uploadPublicMedia
            : MediaHelpers.uploadLargeMedia;

        const res = await uploadMethod({
          data: file,
          fileName: file.name,
        });

        field.onChange(res);
        field.onBlur();
        setIsUploaded(true);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (field.value) {
      setIsUploading(true);
      MediaHelpers.getMediaUrlByIdAsync(field.value)
        .then((res) => {
          setSrc(res);
          setIsUploading(false);
        })
        .catch(() => {
          setSrc('');
          setIsUploading(false);
        });
    }
  }, [field.value]);

  useEffect(() => {
    if (isUploaded) {
      setTimeout(() => {
        setIsUploaded(false);
      }, 2000);
    }
  }, [isUploaded]);

  const isError = fieldState.error && fieldState.isTouched;
  return (
    <>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
          hint={fieldType.span}
        />
      )}{' '}
      <input
        className="input"
        style={{
          display: 'none',
        }}
        accept={fieldType.fieldOptions.types?.toString()}
        ref={InputRef}
        id="contained-button-file"
        type="file"
        onChange={onChange}
      />
      <div className="space-y-4">
        <Button
          bg={isUploaded ? ' bg-green-500' : 'primary'}
          disabled={disabled}
          color={'white'}
          loading={isLoading}
          title={isUploaded ? 'Done' : `Upload ${fieldType.label}`}
          suffix={isUploaded ? <GoCheck /> : undefined}
          onClick={() => {
            InputRef.current?.click();
          }}
        ></Button>
        {isUploading ? (
          <div className="flex justify-center text-center">
            جاري التحميل ...
          </div>
        ) : (
          src && !isLoading && getFileView()
        )}
      </div>
    </>
  );
};

export default FormUploadInput;
